import React, { useEffect, useState } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';

/**
 * Floating "scroll to top" button.
 *
 * - Appears with fade-in once the user has scrolled past `threshold` (default 300px).
 * - Smooth scroll to 0 on click.
 * - Optional `withScrollDown` prop adds a secondary "go to bottom" button
 *   (used on the landing page).
 *
 * Reusable. Performance-safe: scroll listener is throttled with
 * `requestAnimationFrame`.
 */
export default function ScrollToTopButton({ threshold = 300, withScrollDown = false }) {
    const [visible, setVisible] = useState(false);
    const [atBottom, setAtBottom] = useState(false);

    useEffect(() => {
        let ticking = false;

        const check = () => {
            const y = window.scrollY || window.pageYOffset;
            setVisible(y > threshold);

            if (withScrollDown) {
                const reachedBottom = (window.innerHeight + y) >= (document.documentElement.scrollHeight - 80);
                setAtBottom(reachedBottom);
            }
            ticking = false;
        };

        const onScroll = () => {
            if (!ticking) {
                window.requestAnimationFrame(check);
                ticking = true;
            }
        };

        check();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, [threshold, withScrollDown]);

    const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });
    const scrollToBottom = () =>
        window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' });

    const baseBtn =
        'w-12 h-12 rounded-full bg-[#002FA7] text-white shadow-lg shadow-[#002FA7]/25 ' +
        'flex items-center justify-center hover:bg-[#002480] hover:scale-110 ' +
        'transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#002FA7]/40';

    return (
        <div
            className="fixed bottom-20 right-6 z-50 flex flex-col gap-3 print:hidden"
            aria-hidden={!visible && !withScrollDown}
        >
            {withScrollDown && !atBottom && (
                <button
                    type="button"
                    onClick={scrollToBottom}
                    aria-label="Descendre en bas de page"
                    className={`${baseBtn} ${visible ? 'opacity-100' : 'opacity-90'}`}
                    data-testid="scroll-to-bottom-btn"
                >
                    <ChevronDown className="w-5 h-5" />
                </button>
            )}

            <button
                type="button"
                onClick={scrollToTop}
                aria-label="Retour en haut"
                className={`${baseBtn} ${visible ? 'opacity-100 pointer-events-auto translate-y-0'
                    : 'opacity-0 pointer-events-none translate-y-2'}`}
                data-testid="scroll-to-top-btn"
            >
                <ChevronUp className="w-5 h-5" />
            </button>
        </div>
    );
}
